<template>
  <component :is="modalComponent" v-if="show || modalOnly">
    <FocusTrap :active="show">
      <div
        class="m-modal"
        :class="[
          {
            'm-modal--has-header': !!$slots.header,
            'm-modal--has-footer': !!$slots.footer,
            'm-modal--has-close-button-only': showCloseButton && !$slots.header,
            'm-modal--allow-overflow': allowOverflow,
            [`m-modal--padding-${padding}`]: padding !== 'default',
          },
          `m-modal--size-${size}`,
        ]"
        :role="role"
        aria-modal="true"
        :aria-label="ariaLabel"
        :aria-describedby="ariaDescribedby"
        tabindex="-1"
        @click.self="clickaway && closeModal()"
      >
        <div class="m-modal__dialog" role="document">
          <MSkin :skin="headerStyle">
            <div
              v-if="!!$slots.header || showCloseButton"
              class="m-modal__header"
            >
              <!-- @slot Modal header -->
              <div class="m-modal__header-content">
                <slot name="header" />
              </div>
              <div v-if="showCloseButton" class="m-modal__close">
                <MButtonIcon
                  type="button"
                  :aria-label="closeLabel"
                  data-qa-locator="modal-close-btn"
                  icon="x"
                  size="h4"
                  @click="closeModal"
                />
              </div>
            </div>
          </MSkin>
          <div class="m-modal__body" tabindex="0">
            <slot />
          </div>
          <div v-if="!!$slots.footer" class="m-modal__footer">
            <!-- @slot Modal footer -->
            <slot name="footer" />
          </div>
        </div>
      </div>
    </FocusTrap>
  </component>
</template>

<script>
import { FocusTrap } from 'focus-trap-vue'
import { defineComponent } from 'vue'

import useI18n from '../../../composables/useI18n'
import MButtonIcon from '../../controls/ButtonIcon/MButtonIcon'
import MSkin from '../../skins/Skin/MSkin'

import sv from './locales/sv.json'
import MModalRenderer from './Renderer/MModalRenderer.vue'

/**
 * Modal window component. Can be used either with the modal composable, or standalone.
 */
export default defineComponent({
  name: 'MModal',
  components: { FocusTrap, MButtonIcon, MSkin, MModalRenderer },
  props: {
    /**
     * Whether to show the close button
     */
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether to close on escape button key press
     */
    closeOnEscape: {
      type: Boolean,
      default: true,
    },
    /**
     * Omits `MModalRenderer` from the template.
     * By default the modal has overlays, animation and scroll logic included, but detaching it
     * is useful in some compositions to allow `MModal` to be displayed conditionally with v-if slotted inside `MModalRenderer`.
     */
    modalOnly: {
      type: Boolean,
      default: false,
    },
    /** Whether the modal is visible */
    /**
     * The size of the modal
     *
     * small: actionable short messages
     * medium: long messages that do not require an action
     * large: long paragraphs that include other headings, inline notifications and body copy
     */
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value),
    },
    /**
     * Whether the modal is rendered
     */
    show: {
      type: Boolean,
      default: false,
    },
    /**
     * Whether the modal can be dimissed by clicking on the overlay
     */
    clickaway: {
      type: Boolean,
      default: true,
    },
    /**
     * The modal ARIA role.
     *
     * alert: important, time sensitive, message.
     * alertdialog: interrupt a user's workflow to communicate an important message and require a response
     * dialog: separate content or UI from the rest of the web application or page
     */
    role: {
      type: String,
      default: 'dialog',
      validator: (value) => ['dialog', 'alert', 'alertdialog'].includes(value),
    },
    /**
     * The modal ARIA Label, should match the title
     */
    ariaLabel: {
      type: String,
      required: true,
    },
    /**
     * The modal ARIA describedby, should point at the title or content element id
     */
    ariaDescribedby: {
      type: String,
      default: null,
    },
    /**
     * Header theme switch
     * - affects close buton and header text color
     */
    headerStyle: {
      type: String,
      default: null,
      validator: (value) => ['light', 'dark'].includes(value),
    },
    /**
     * Allow elements to overflow the modal
     * This prevents internal scrolling.
     * Ideally only for use in short modals.
     * Useful for MSelectInput dropdowns.
     */
    allowOverflow: {
      type: Boolean,
      default: false,
    },
    /**
     * Set the modal padding
     */
    padding: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'none'].includes(value),
    },
  },
  emits: ['close-modal'],
  setup() {
    const { t } = useI18n()
    return { t }
  },
  i18n: {
    messages: {
      sv,
    },
  },
  computed: {
    closeLabel() {
      return this.t ? this.t('MModal.close', 'Close') : 'Close'
    },
    modalComponent() {
      return this.modalOnly ? 'div' : 'MModalRenderer'
    },
  },
  watch: {
    show: {
      immediate: true,
      handler() {
        if (this.show) {
          document.addEventListener('keyup', this.keyupHandler)
        } else {
          document.removeEventListener('keyup', this.keyupHandler)
        }
      },
    },
  },
  methods: {
    keyupHandler(e) {
      if (e.key === 'Escape' && this.closeOnEscape) {
        this.closeModal()
      }
    },
    /**
     * Emit the close modal event
     */
    closeModal() {
      /**
       * Close modal event
       * @type {string}
       * @event 'close-modal'
       */
      this.$emit('close-modal')
    },
  },
})
</script>

<style lang="scss" scoped>
@use 'sass:math';
@use '../../../sass/framework/mixins/breakpoint';
@use '../../../sass/framework/mixins/font-size';

@mixin modal-margin {
  // This forces a margin above and below the overflowed modal
  &::after,
  &::before {
    content: '';
    display: block;
    flex-shrink: 0;
    height: var(--spacing-32);
    width: 100%;
  }
}

.m-modal {
  --modal-border-radius: var(--radius-md);
  @include modal-margin;
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  padding: 0 var(--spacing-24);
  position: fixed;
  right: 0;
  top: 0;
  z-index: calc(var(--z-index-modal-overlay) + 1);

  &__dialog {
    background-color: var(--skin-background-light);
    border-radius: var(--modal-border-radius);
    box-shadow: 0 32px 40px 0 rgba(0, 0, 0, 0.1); // Custom modal shadow
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    margin: auto;
    max-height: calc(100% - var(--spacing-64));
    /* Stylelint does not know about the new dvh unit */
    /* stylelint-disable-next-line unit-no-unknown */
    max-height: calc(100dvh - var(--spacing-64));
    max-width: 480px;
    overflow: hidden;
    position: relative;
    width: 100%;
  }

  &__header {
    align-items: center;
    background: var(--skin-background);
    border-radius: var(--modal-border-radius) var(--modal-border-radius) 0 0;
    display: flex;
    justify-content: flex-end;
    padding: var(--spacing-32) var(--spacing-24) 0;

    @include breakpoint.breakpoint(tablet-up) {
      padding: var(--spacing-32) var(--spacing-32) 0;
    }

    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5) {
      &:only-child {
        margin: 0;
      }
    }

    &-content {
      flex: 1 1 auto;
    }
  }

  &__close {
    @include font-size.font-size(h4);
    align-items: center;
    display: flex;
    height: 1em;
    justify-content: center;
    width: 1em;
  }

  &__body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: var(--spacing-32) var(--spacing-24);

    @include breakpoint.breakpoint(tablet-up) {
      padding: var(--spacing-32) var(--spacing-32);
    }
  }

  .m-modal__footer {
    padding: var(--spacing-12) var(--spacing-24) var(--spacing-32)
      var(--spacing-24);

    @include breakpoint.breakpoint(tablet-up) {
      padding: var(--spacing-12) var(--spacing-32) var(--spacing-32)
        var(--spacing-32);
    }
  }

  // Alignment
  &--top {
    .m-modal__dialog {
      margin: 0 auto auto auto;
    }
  }

  &--bottom {
    .m-modal__dialog {
      margin: auto auto 0 auto;
    }
  }

  // Size
  &--size {
    // Small is default

    &-medium {
      .m-modal__dialog {
        max-width: 600px;
      }
    }

    &-large {
      .m-modal__dialog {
        max-width: 800px;
      }
    }
  }

  &--has-header {
    .m-modal__header {
      border-bottom: 1px solid var(--border-separator);
      padding: var(--spacing-16) var(--spacing-24);

      @include breakpoint.breakpoint(tablet-up) {
        padding: var(--spacing-24) var(--spacing-32);
      }
    }

    .m-modal__body {
      padding-top: var(--spacing-24);
    }
  }

  &--has-footer {
    .m-modal__body {
      padding-bottom: var(--spacing-12);
    }
  }

  &--has-close-button-only {
    .m-modal__body {
      padding-top: 0;
    }
  }

  &--allow-overflow {
    overflow-y: auto;

    .m-modal__dialog,
    .m-modal__body {
      max-height: none;
      overflow: visible;
    }
  }

  &--padding-none {
    .m-modal__body {
      padding: 0;
    }
  }
}
</style>
